/**
 * Earth Ultra style scroll-sphere engine (canvas characters → starfield).
 * Ported from earth_ultra.html; returns cleanup().
 */
export function initLandingSphere(root) {
  const canvas = root.querySelector('#orb');
  if (!canvas) return () => {};
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stageEl = root.querySelector('#stage');
  const introEl = root.querySelector('#intro');
  const vign = root.querySelector('#vignette');
  const progressEl = root.querySelector('#progress');
  const starsMsg = root.querySelector('#starsMsg');
  const autoBtn = root.querySelector('#autoScrollBtn');
  const panels = Array.from(root.querySelectorAll('.panel'));
  const tags = Array.from(root.querySelectorAll('.beacon-tag'));

  const CH = '{}[]()<>/;:=+-*&|!?$#01';
  const charState = [];
  const starSeeds = [];

  const FOCUS = [0.18, 0.4, 0.62];
  const YAWT = [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6];
  const FOCUS_HOLD = 0.055;
  const FOCUS_RAMP = 0.045;
  const BAZ = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3];
  const STAGE_TILT = 0.42;
  const GROW_START = 0.72;
  const BURST_START = 0.84;

  let cssW = 0;
  let cssH = 0;
  let idleT = 0;
  let raf = 0;
  let smoothY = null;
  let sTopC = 0;
  let sScrollC = 1;
  let t0 = 0;
  let destroyed = false;

  const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (a) => a * a * (3 - 2 * a);
  const easeOut = (x) => 1 - Math.pow(1 - x, 3);

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    cssW = window.innerWidth;
    cssH = window.innerHeight;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (stageEl) {
      sTopC = stageEl.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
      sScrollC = Math.max(1, stageEl.offsetHeight - cssH);
    }
  };
  resize();

  const yawAt = (p) => {
    const last = FOCUS.length - 1;
    const h = FOCUS_HOLD;
    if (p <= FOCUS[0]) return YAWT[0];
    if (p >= FOCUS[last]) return YAWT[last];
    for (let i = 0; i < last; i++) {
      if (p <= FOCUS[i] + h) return YAWT[i];
      if (p < FOCUS[i + 1] - h) {
        const t = (p - (FOCUS[i] + h)) / (FOCUS[i + 1] - h - (FOCUS[i] + h));
        return lerp(YAWT[i], YAWT[i + 1], smooth(clamp01(t)));
      }
    }
    return YAWT[last];
  };

  const focusInfo = (p) => {
    if (p >= GROW_START) return { i: -1, amt: 0 };
    let best = 0;
    let bd = 9;
    for (let i = 0; i < FOCUS.length; i++) {
      const d = Math.abs(p - FOCUS[i]);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    const a = clamp01(1 - (bd - FOCUS_HOLD) / FOCUS_RAMP);
    return { i: best, amt: smooth(a) };
  };

  const project = (px, py, pz, yaw, tilt, cx, cy, R) => {
    const cyaw = Math.cos(yaw);
    const syaw = Math.sin(yaw);
    const x1 = px * cyaw + pz * syaw;
    const z1 = -px * syaw + pz * cyaw;
    const y1 = py;
    const ct = Math.cos(tilt);
    const st = Math.sin(tilt);
    const y2 = y1 * ct - z1 * st;
    const z2 = y1 * st + z1 * ct;
    return { sx: cx + x1 * R, sy: cy - y2 * R, z: z2 };
  };

  const hash01 = (a, b) => {
    let h = (Math.sin(a * 12.9898 + b * 78.233) * 43758.5453) % 1;
    return h < 0 ? h + 1 : h;
  };

  const ensureStars = (n) => {
    if (starSeeds.length >= n) return;
    for (let i = starSeeds.length; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const elev = (Math.random() - 0.5) * Math.PI * 0.9;
      const speed = 0.35 + Math.random() * 1.4;
      starSeeds.push({
        ax: Math.cos(a) * Math.cos(elev),
        ay: Math.sin(elev),
        az: Math.sin(a) * Math.cos(elev),
        speed,
        twinkle: Math.random() * Math.PI * 2,
        size: 0.6 + Math.random() * 1.8,
        bright: 0.35 + Math.random() * 0.65,
      });
    }
  };

  const draw = (now) => {
    if (destroyed) return;
    if (!t0) t0 = now || 0;
    const le = reduce ? 1 : easeOut(clamp01(((now || 0) - t0) / 900));
    idleT += reduce ? 0 : 0.004;

    const rawY = window.scrollY || window.pageYOffset || 0;
    if (smoothY === null) smoothY = rawY;
    else smoothY += (rawY - smoothY) * 0.14;
    if (Math.abs(rawY - smoothY) < 0.35) smoothY = rawY;

    const y = smoothY;
    const sp = clamp01((y - sTopC) / sScrollC);
    if (progressEl) progressEl.style.width = `${(sp * 100).toFixed(2)}%`;

    const fi = focusInfo(sp);
    const m = clamp01(sp / 0.1);
    const grow = clamp01((sp - GROW_START) / (BURST_START - GROW_START));
    const bp = clamp01((sp - BURST_START) / (1 - BURST_START));
    const starPhase = smooth(bp);

    const baseR = Math.min(cssW, cssH) * 0.36;
    const focusZoom = lerp(1, 2.45, fi.amt);
    const growZoom = 1 + grow * 2.8 + starPhase * 1.2;
    const R = baseR * focusZoom * growZoom * (1 + (1 - le) * 0.15);

    const cx = cssW * 0.5;
    const cy = cssH * 0.5;
    const heroTilt = 0.48 + Math.sin(idleT * 0.55) * 0.22;
    const tilt = lerp(heroTilt, STAGE_TILT, m);
    const idleYaw = -Math.PI / 2 + idleT * 0.85;
    const targetYaw =
      sp < GROW_START ? yawAt(sp) : YAWT[YAWT.length - 1] + grow * 0.9 + starPhase * 1.6;
    let yaw = lerp(idleYaw, targetYaw, m);

    const Rdraw = R * (1 + (1 - le) * 4.2);
    yaw += (1 - le) * 2.8;

    const sphereAlpha = lerp(0.95, 0.58, fi.amt) * le * (1 - starPhase * 0.15);

    let dx = 0;
    let dy = 0;
    if (fi.amt > 0.001 && fi.i >= 0) {
      const fb = project(Math.cos(BAZ[fi.i]), 0, Math.sin(BAZ[fi.i]), yaw, tilt, cx, cy, R);
      dx = fi.amt * (cssW * 0.38 - fb.sx);
      dy = fi.amt * (cssH * 0.46 - fb.sy);
    }
    if (grow > 0) {
      dx *= 1 - grow;
      dy *= 1 - grow;
    }
    const ocx = cx + dx;
    const ocy = cy + dy;

    ctx.clearRect(0, 0, cssW, cssH);
    ctx.font = '12px ui-monospace, SF Mono, Menlo, Consolas, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const pts = [];
    let pi = 0;
    const step = cssW < 700 ? 0.14 : 0.1;

    for (let phi = 0; phi < Math.PI * 2; phi += step) {
      for (let th = 0.0001; th < Math.PI; th += step) {
        const pr = project(
          Math.sin(th) * Math.cos(phi),
          Math.cos(th),
          Math.sin(th) * Math.sin(phi),
          yaw,
          tilt,
          ocx,
          ocy,
          Rdraw
        );
        let sx = pr.sx;
        let sy = pr.sy;
        let zOut = pr.z;

        if (bp > 0) {
          ensureStars(pi + 1);
          const seed = starSeeds[pi];
          const hsh = hash01(phi, th);
          const hsh2 = hash01(phi * 1.7, th * 2.3);
          const ex = bp * (1.2 + hsh * 3.5);
          const eX = ocx + (pr.sx - ocx) * (1 + ex) + (hsh - 0.5) * bp * cssW * 0.25;
          const eY = ocy + (pr.sy - ocy) * (1 + ex) + (hsh2 - 0.5) * bp * cssH * 0.2;
          const spread = Math.max(cssW, cssH) * (0.55 + seed.speed * 0.75);
          let starX = ocx + seed.ax * spread + Math.sin(idleT * 0.3 + seed.twinkle) * 8;
          let starY = ocy + seed.ay * spread + Math.cos(idleT * 0.25 + seed.twinkle) * 6;
          starX += seed.az * spread * 0.15;
          const tc = smooth(clamp01((bp - 0.08) / 0.55));
          sx = eX + (starX - eX) * tc;
          sy = eY + (starY - eY) * tc;
          zOut = pr.z * (1 - tc) + seed.az * tc;
        }

        if (charState[pi] === undefined || (!reduce && Math.random() < 0.04)) {
          charState[pi] = CH[(Math.random() * CH.length) | 0];
        }
        pts.push({
          x: sx,
          y: sy,
          z: zOut,
          c: charState[pi],
          i: pi,
          h: hash01(phi * 3.1, th * 5.7),
        });
        pi++;
      }
    }

    ensureStars(pts.length);
    pts.sort((a, b) => a.z - b.z);

    for (let i = 0; i < pts.length; i++) {
      const pt = pts[i];
      const near = (pt.z + 1) / 2;
      let a = (0.22 + near * 0.72) * sphereAlpha;

      if (bp > 0) {
        const seed = starSeeds[pt.i] || { bright: 0.7, twinkle: 0, size: 1 };
        const tw = 0.55 + 0.45 * Math.sin(idleT * 2.2 + seed.twinkle);
        a = (0.15 + seed.bright * 0.85 * tw) * le * clamp01(0.35 + starPhase);
        if (starPhase > 0.55) {
          const dotA = a * clamp01((starPhase - 0.55) / 0.35);
          const charA = a * (1 - clamp01((starPhase - 0.55) / 0.4));
          const br = 180 + Math.round(near * 75);
          const bg = 160 + Math.round(near * 70);
          const bb = 120 + Math.round(seed.bright * 80);
          ctx.fillStyle = `rgba(${br},${bg},${bb},${Math.min(1, dotA).toFixed(3)})`;
          const r = seed.size * (0.6 + starPhase * 1.4);
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, r, 0, 6.2832);
          ctx.fill();
          if (seed.bright > 0.7 && starPhase > 0.65) {
            const g = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, r * 5);
            g.addColorStop(0, `rgba(255,220,170,${(0.18 * seed.bright * tw).toFixed(3)})`);
            g.addColorStop(1, 'rgba(255,180,100,0)');
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, r * 5, 0, 6.2832);
            ctx.fill();
          }
          if (charA > 0.02) {
            ctx.fillStyle = `rgba(255,${150 + Math.round(near * 80)},${95 + Math.round(near * 70)},${charA.toFixed(3)})`;
            ctx.fillText(pt.c, pt.x, pt.y);
          }
          continue;
        }
      }

      ctx.fillStyle = `rgba(255,${150 + Math.round(near * 80)},${95 + Math.round(near * 70)},${a.toFixed(3)})`;
      ctx.fillText(pt.c, pt.x, pt.y);
    }

    if (bp > 0 && bp < 0.55) {
      const sw = bp / 0.55;
      ctx.strokeStyle = `rgba(255,180,120,${((1 - sw) * 0.55).toFixed(3)})`;
      ctx.lineWidth = 2 + (1 - sw) * 6;
      ctx.beginPath();
      ctx.arc(ocx, ocy, sw * Math.max(cssW, cssH) * 0.7, 0, 6.2832);
      ctx.stroke();
    }

    const bpos = [];
    for (let b = 0; b < BAZ.length; b++) {
      const bpt = project(Math.cos(BAZ[b]), 0, Math.sin(BAZ[b]), yaw, tilt, ocx, ocy, R);
      bpos.push(bpt);
      if (bp > 0.15 || grow > 0.85) {
        if (tags[b]) tags[b].style.opacity = '0';
        continue;
      }
      if (!(bpt.z > -0.12) || !isFinite(bpt.sx) || !isFinite(bpt.sy)) {
        if (tags[b]) tags[b].style.opacity = '0';
        continue;
      }
      const front = clamp01((bpt.z + 0.1) / 1.0);
      const active = b === fi.i ? fi.amt : 0;
      const glow = clamp01((m * (0.4 + 0.55 * front) + active * 0.9) * le * (1 - grow));
      const rad = 7 + front * 8 + active * 30;
      const g = ctx.createRadialGradient(bpt.sx, bpt.sy, 0, bpt.sx, bpt.sy, rad);
      g.addColorStop(0, `rgba(255,220,170,${glow.toFixed(3)})`);
      g.addColorStop(0.4, `rgba(245,150,85,${(0.55 * glow).toFixed(3)})`);
      g.addColorStop(1, 'rgba(245,150,85,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(bpt.sx, bpt.sy, rad, 0, 6.2832);
      ctx.fill();
      ctx.fillStyle = `rgba(255,242,222,${glow.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(bpt.sx, bpt.sy, 2.2 + active * 2.4, 0, 6.2832);
      ctx.fill();

      if (active > 0.18) {
        const hr = 70 + active * 240;
        const hg = ctx.createRadialGradient(bpt.sx, bpt.sy, 0, bpt.sx, bpt.sy, hr);
        hg.addColorStop(0, `rgba(235,135,70,${(0.12 * active).toFixed(3)})`);
        hg.addColorStop(1, 'rgba(235,135,70,0)');
        ctx.fillStyle = hg;
        ctx.beginPath();
        ctx.arc(bpt.sx, bpt.sy, hr, 0, 6.2832);
        ctx.fill();
        for (let ri = 0; ri < 3; ri++) {
          const ph = (idleT * 0.45 + ri / 3) % 1;
          ctx.strokeStyle = `rgba(235,135,70,${((1 - ph) * 0.4 * active).toFixed(3)})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(bpt.sx, bpt.sy, 16 + ph * 140, 0, 6.2832);
          ctx.stroke();
        }
      }

      if (tags[b]) {
        tags[b].style.opacity = (m * front * (1 - fi.amt) * (1 - grow)).toFixed(3);
        tags[b].style.transform = `translate(${bpt.sx.toFixed(1)}px,${bpt.sy.toFixed(1)}px)`;
      }
    }

    if (introEl) {
      introEl.style.opacity = clamp01(1 - sp / 0.09).toFixed(3);
    }

    const isMobile = cssW < 720;
    for (let k = 0; k < panels.length; k++) {
      const on = k === fi.i ? fi.amt : 0;
      panels[k].style.opacity = on.toFixed(3);
      if (on > 0.001 && bpos[k] && !isMobile) {
        let px2 = Math.min(bpos[k].sx + 72, cssW - 48);
        const py2 = bpos[k].sy;
        if (px2 + 320 > cssW) px2 = Math.max(24, bpos[k].sx - 340);
        panels[k].style.transform = `translate(${px2.toFixed(1)}px,${py2.toFixed(1)}px) translateY(-50%)`;
      } else if (on > 0.001 && isMobile) {
        panels[k].style.transform = 'translate(-50%, 0)';
        panels[k].style.left = '50%';
        panels[k].style.bottom = '14%';
        panels[k].style.top = 'auto';
      }
    }

    if (starsMsg) {
      const sa = starPhase > 0.55 ? clamp01((starPhase - 0.55) / 0.3) : 0;
      starsMsg.style.opacity = sa.toFixed(3);
      starsMsg.classList.toggle('is-interactive', sa > 0.5);
    }

    if (vign) {
      let vo = 0;
      if (fi.amt > 0) vo = 0.25 + fi.amt * 0.45;
      else if (grow > 0 && bp < 0.5) vo = grow * 0.35;
      else if (bp > 0) vo = (1 - starPhase) * 0.2;
      vign.style.opacity = vo.toFixed(3);
    }

    if (!reduce) raf = requestAnimationFrame(draw);
  };

  if (reduce) {
    draw(0);
    if (introEl) introEl.style.opacity = '1';
  } else {
    raf = requestAnimationFrame(draw);
  }

  /* Auto-scroll: 「继续」 at 1.5× earth_ultra base speed (420 → 630 px/s) */
  let autoCleanup = () => {};
  if (autoBtn && stageEl) {
    let playing = false;
    let rafId = 0;
    let lastTs = 0;
    const SPEED = reduce ? 900 : 630; // 1.5× of 420
    let ignoreScrollUntil = 0;

    const maxScroll = () => {
      const doc = document.documentElement;
      return Math.max(0, (doc.scrollHeight || document.body.scrollHeight) - window.innerHeight);
    };
    const nearBottom = () => (window.scrollY || window.pageYOffset || 0) >= maxScroll() - 4;

    const setPlaying = (on) => {
      playing = on;
      autoBtn.classList.toggle('is-playing', on);
      autoBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      autoBtn.setAttribute('aria-label', on ? '暂停' : '继续');
      if (!on) {
        lastTs = 0;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
      }
    };

    const tick = (ts) => {
      if (!playing || destroyed) return;
      if (!lastTs) lastTs = ts;
      const dt = Math.min(0.05, (ts - lastTs) / 1000);
      lastTs = ts;
      const y = window.scrollY || window.pageYOffset || 0;
      const next = y + SPEED * dt;
      const cap = maxScroll();
      if (next >= cap) {
        ignoreScrollUntil = performance.now() + 120;
        window.scrollTo(0, cap);
        setPlaying(false);
        autoBtn.classList.add('is-done');
        const idle = autoBtn.querySelector('.label-idle');
        if (idle) idle.textContent = '已到底部';
        return;
      }
      ignoreScrollUntil = performance.now() + 80;
      window.scrollTo(0, next);
      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (nearBottom()) {
        ignoreScrollUntil = performance.now() + 120;
        window.scrollTo(0, 0);
        autoBtn.classList.remove('is-done');
        const idle = autoBtn.querySelector('.label-idle');
        if (idle) idle.textContent = '继续';
      }
      setPlaying(true);
      rafId = requestAnimationFrame(tick);
    };

    const stop = () => setPlaying(false);

    const onClick = () => {
      if (playing) stop();
      else start();
    };

    const cancelOnUser = () => {
      if (!playing) return;
      if (performance.now() < ignoreScrollUntil) return;
      stop();
    };

    const onKey = (e) => {
      if (!playing) return;
      const k = e.key;
      if (
        k === 'ArrowDown' ||
        k === 'ArrowUp' ||
        k === 'PageDown' ||
        k === 'PageUp' ||
        k === ' ' ||
        k === 'Home' ||
        k === 'End'
      ) {
        cancelOnUser();
      }
    };

    const onScroll = () => {
      if (playing) return;
      if (!autoBtn.classList.contains('is-done')) return;
      if (!nearBottom()) {
        autoBtn.classList.remove('is-done');
        const idle = autoBtn.querySelector('.label-idle');
        if (idle) idle.textContent = '继续';
      }
    };

    autoBtn.addEventListener('click', onClick);
    window.addEventListener('wheel', cancelOnUser, { passive: true });
    window.addEventListener('touchstart', cancelOnUser, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });

    autoCleanup = () => {
      stop();
      autoBtn.removeEventListener('click', onClick);
      window.removeEventListener('wheel', cancelOnUser);
      window.removeEventListener('touchstart', cancelOnUser);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
    };
  }

  window.addEventListener('resize', resize);
  window.addEventListener('load', resize);

  return () => {
    destroyed = true;
    cancelAnimationFrame(raf);
    autoCleanup();
    window.removeEventListener('resize', resize);
    window.removeEventListener('load', resize);
  };
}
